/**
 * @fileoverview Event block — coloured block rendered in the day/week
 *               time grid for a timed event. Adapted from upstream:
 *               removed DnD/dialog wrappers; clicking the block fires
 *               `onEventClick`.
 */

import { cva, type VariantProps } from "class-variance-authority";
import { format, differenceInMinutes, parseISO } from "date-fns";
import type { HTMLAttributes } from "react";

import { useCalendar } from "@/calendar/contexts/use-calendar";

import { cn } from "@/utils/misc";

import type { IEvent } from "@/calendar/interfaces";

const eventBlockVariants = cva(
  "flex w-full select-none flex-col gap-0.5 truncate whitespace-nowrap rounded-md border px-2 py-1.5 text-left text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  {
    variants: {
      color: {
        blue: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 [&_.event-dot]:fill-blue-600",
        green:
          "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300 [&_.event-dot]:fill-green-600",
        red: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300 [&_.event-dot]:fill-red-600",
        yellow:
          "border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300 [&_.event-dot]:fill-yellow-600",
        purple:
          "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300 [&_.event-dot]:fill-purple-600",
        orange:
          "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300 [&_.event-dot]:fill-orange-600",
        gray: "border-neutral-200 bg-neutral-50 text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 [&_.event-dot]:fill-neutral-600",
        pink: "border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-800 dark:bg-pink-950 dark:text-pink-300 [&_.event-dot]:fill-pink-600",

        "blue-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-blue-600",
        "green-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-green-600",
        "red-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-red-600",
        "orange-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-orange-600",
        "purple-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-purple-600",
        "yellow-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-yellow-600",
        "gray-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-neutral-600",
        "pink-dot":
          "bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-pink-600",
      },
    },
    defaultVariants: {
      color: "blue-dot",
    },
  },
);

interface IProps
  extends
    HTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof eventBlockVariants>, "color"> {
  event: IEvent;
}

export function EventBlock({ event, className }: IProps) {
  const { badgeVariant, onEventClick } = useCalendar();

  const start = parseISO(event.startDate);
  const end = parseISO(event.endDate);
  const durationInMinutes = differenceInMinutes(end, start);
  const heightInPixels = (durationInMinutes / 60) * 96 - 8;

  const color: VariantProps<typeof eventBlockVariants>["color"] =
    badgeVariant === "dot" ? `${event.color}-dot` : event.color;

  const isPlannedMarker = event.meta?.marker === "planned";
  const classes = cn(
    eventBlockVariants({ color }),
    durationInMinutes < 35 && "justify-center py-0",
    isPlannedMarker && "border-dashed bg-transparent opacity-75",
    className,
  );

  return (
    <button
      type="button"
      className={classes}
      style={{ height: `${heightInPixels}px` }}
      onClick={() => onEventClick(event)}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {["mixed", "dot"].includes(badgeVariant) && (
          <svg
            width="8"
            height="8"
            viewBox="0 0 8 8"
            className="event-dot shrink-0"
          >
            <circle cx="4" cy="4" r="4" />
          </svg>
        )}

        <p className="min-w-0 flex-1 truncate font-semibold">
          {isPlannedMarker && (
            <span className="mr-0.5 text-[10px] font-medium uppercase tracking-wide opacity-70">
              Plan ·{" "}
            </span>
          )}
          {event.title}
        </p>
      </div>

      {durationInMinutes > 25 && (
        <p className="text-[10px] opacity-80">
          {format(start, "h:mm a")} - {format(end, "h:mm a")}
        </p>
      )}
    </button>
  );
}
